// =============================================================================
// src/views/mapa-eleitoral/controls.ts — Binding dos controles da Tela 01.
//
// Proveniencia: assets/unified-app.js Sections.mapaEleitoral._bindControls /
//   _setYear (linhas 935-984). Liga layer-thumbs, markers da timeline, botao de
//   play e botoes de velocidade, e aplica o efeito de _setYear no DOM (marker
//   ativo + largura do progress). Usa o mesmo padrao bindOnce (flag por elemento)
//   para nao re-bindar ao reentrar na secao.
//
// SRP: so o glue DOM<->player/estilos. Markup em markup.ts; relogio em playback.ts.
// =============================================================================

import type { MapController, ElectoralYear, LegendLayerKey } from '../../app/types';
import { MapaTimelinePlayback } from './playback';
import { applyMapaEleitoralStyles } from './styles';
import { updateInstantFloat, updateLegendFloat } from './overlays';

/** Avanco em % por slot da timeline (idx * 20), preservado de _setYear. */
const TIMELINE_PROGRESS_STEP_PCT = 20;

/**
 * Liga um listener uma unica vez por elemento+evento+chave (replica UI.bindOnce).
 * Usa uma flag `__bound_<evento>_<chave>` no proprio elemento — identico ao
 * original — para sobreviver a reativacoes da secao sem duplicar handlers.
 */
function bindMapaControlOnce(
  element: Element | null,
  eventName: string,
  bindingKey: string,
  listener: EventListener,
): void {
  if (!element) return;
  const boundFlag = '__bound_' + eventName + '_' + bindingKey;
  const flaggedElement = element as Element & Record<string, boolean>;
  if (flaggedElement[boundFlag]) return;
  flaggedElement[boundFlag] = true;
  element.addEventListener(eventName, listener);
}

/** Aplica o ano no DOM: marker ativo + largura do progress (parte visual de _setYear). */
function applyYearToTimelineDom(year: ElectoralYear, timelineYears: readonly ElectoralYear[]): void {
  document.querySelectorAll('.u-tl-marker').forEach((markerNode) => {
    markerNode.classList.remove('active');
  });
  const activeMarker = document.querySelector('.u-tl-marker[data-year="' + year + '"]');
  if (activeMarker) activeMarker.classList.add('active');
  const progressBar = document.getElementById('u-tl-progress');
  if (progressBar) {
    progressBar.style.width = timelineYears.indexOf(year) * TIMELINE_PROGRESS_STEP_PCT + '%';
  }
}

/** Liga os 4 layer-thumbs: troca camada, reaplica estilos e atualiza legenda. */
function bindLayerThumbs(
  mapCtrl: MapController,
  setCurrentLayer: (layer: LegendLayerKey) => void,
  getCurrentLayer: () => LegendLayerKey,
  getCurrentYear: () => ElectoralYear,
): void {
  document.querySelectorAll('.u-layer-thumb').forEach((thumb) => {
    bindMapaControlOnce(thumb, 'click', 'mapa-layer', () => {
      document.querySelectorAll('.u-layer-thumb').forEach((t) => t.classList.remove('active'));
      thumb.classList.add('active');
      const pickedLayer = (thumb as HTMLElement).dataset.layer as LegendLayerKey;
      setCurrentLayer(pickedLayer);
      applyMapaEleitoralStyles(mapCtrl, getCurrentLayer, getCurrentYear);
      updateLegendFloat(pickedLayer);
    });
  });
}

/** Liga os markers da timeline: clicar num ano chama player.setYear (efeito _setYear). */
function bindTimelineMarkers(player: MapaTimelinePlayback): void {
  document.querySelectorAll('.u-tl-marker').forEach((markerNode) => {
    bindMapaControlOnce(markerNode, 'click', 'mapa-timeline', () => {
      const markerYear = (markerNode as HTMLElement).dataset.year as ElectoralYear;
      player.setYear(markerYear);
    });
  });
}

/** Liga o botao de play (#u-playBtn) ao toggle do player. */
function bindPlayButton(player: MapaTimelinePlayback): void {
  bindMapaControlOnce(document.getElementById('u-playBtn'), 'click', 'mapa-play', () => {
    player.togglePlay();
  });
}

/** Liga os botoes de velocidade: troca speed e reinicia o loop se estiver tocando. */
function bindSpeedButtons(player: MapaTimelinePlayback): void {
  document.querySelectorAll('.u-speed-btn').forEach((speedBtn) => {
    bindMapaControlOnce(speedBtn, 'click', 'mapa-speed', () => {
      document.querySelectorAll('.u-speed-btn').forEach((b) => b.classList.remove('active'));
      speedBtn.classList.add('active');
      player.speed = parseInt((speedBtn as HTMLElement).dataset.speed ?? '', 10) || 1;
      if (player.isPlaying) player.restartInterval();
    });
  });
}

/** Troca o icone do play para "pause" (estado tocando), replicando _togglePlay. */
function setPlayIconToPause(): void {
  const playBtn = document.getElementById('u-playBtn');
  if (playBtn) playBtn.innerHTML = '<i class="ti ti-player-pause"></i>';
}

/** Troca o icone do play para "play" (estado parado), replicando _togglePlay/stop. */
function setPlayIconToPlay(): void {
  const playBtn = document.getElementById('u-playBtn');
  if (playBtn) playBtn.innerHTML = '<i class="ti ti-player-play"></i>';
}

/**
 * Cria o player da timeline com os hooks ja ligados ao DOM e aos estilos.
 * onYearAdvance reproduz _setYear por inteiro (marker+progress+estilos+instant).
 */
export function createMapaTimelinePlayback(
  mapCtrl: MapController,
  getCurrentLayer: () => LegendLayerKey,
): MapaTimelinePlayback {
  const player = new MapaTimelinePlayback({
    onYearAdvance: (year) => {
      applyYearToTimelineDom(year, player.years);
      applyMapaEleitoralStyles(mapCtrl, getCurrentLayer, () => player.currentYear);
      updateInstantFloat(year);
    },
    onPlayStart: setPlayIconToPause,
    onPlayStop: setPlayIconToPlay,
  });
  return player;
}

/** Liga todos os controles da secao (replica _bindControls na ordem original). */
export function bindMapaEleitoralControls(
  mapCtrl: MapController,
  player: MapaTimelinePlayback,
  setCurrentLayer: (layer: LegendLayerKey) => void,
  getCurrentLayer: () => LegendLayerKey,
): void {
  bindLayerThumbs(mapCtrl, setCurrentLayer, getCurrentLayer, () => player.currentYear);
  bindTimelineMarkers(player);
  bindPlayButton(player);
  bindSpeedButtons(player);
}
